/**
 * @fileoverview Loading spinner components for indicating loading states throughout the application
 * @active Used in multiple components across the application
 *
 * This file provides two key components:
 * 1. WaveSpinner - A basic bouncing animation spinner
 * 2. WaveSpinnerBlock - A complete loading indicator with spinner and text
 *
 * The WaveSpinner creates a set of circles that animate up and down in sequence,
 * creating a wave-like effect. The WaveSpinnerBlock wraps this with a label and
 * proper container styling.
 *
 * These components are designed to provide immediate visual feedback during
 * loading states and async operations.
 *
 * @see {@link src/components/companies/companiesList/newCompaniesList/index.tsx} - Primary usage in companies list
 * @see {@link src/store/newCompaniesList/newCompaniesList.slice.ts} - Loading state management
 */

import {
  Box,
  Flex,
  Text,
  type BoxProps,
  type FlexProps,
  type TextProps,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";

import { h_700_16_100_32 } from "@/styles/fontStyles";

/**
 * Props for the WaveSpinner component
 */
interface WaveSpinnerProps {
  /** Width of the container for the spinner */
  containerWidth?: string;
  /** Size of each bouncing ball */
  ballSize?: string;
  /** Array of colors for each ball */
  colors?: string[];
  /** Duration of the animation in seconds */
  animationDuration?: string;
  /** Height of the bounce animation */
  animationHeight?: string;
}

/**
 * Props for the WaveSpinnerBlock component
 */
interface WaveSpinnerBlockProps {
  /** Title/text to display below the spinner */
  title?: string;
  /** Configuration parameters for the spinner */
  spinnerParams?: WaveSpinnerProps;
  /** Additional style props for the container */
  styleBlock?: FlexProps;
  /** Additional style props for the title text */
  styleTitle?: TextProps;
}

/**
 * Creates a keyframe animation for the bounce effect
 *
 * @param animationHeight - The height of the bounce in any CSS unit
 * @returns Keyframe animation definition
 */
const bounceAnimation = (animationHeight: string) => keyframes`
  0%, 100% {transform: translateY(0);}
  50% {transform: translateY(-${animationHeight});}
`;

/**
 * Wave Spinner component
 *
 * Creates an animated wave effect with bouncing circles
 *
 * @param props - WaveSpinner props
 */
export const WaveSpinner = ({
  containerWidth = "110px",
  ballSize = "20px",
  colors = ["#7A99B9", "#4E7AA7", "#366A9F", "#24578A"],
  animationDuration = "0.8",
  animationHeight = "30px",
}: WaveSpinnerProps) => {
  const waveAnimation = bounceAnimation(animationHeight);

  const styleContainer: BoxProps = {
    width: containerWidth,
    minH: parseInt(ballSize) + parseInt(animationHeight) + "px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    gap: "10px",
  };

  const styleBall = (color: string, index: number): BoxProps => {
    return {
      bg: color,
      w: ballSize,
      h: ballSize,
      borderRadius: "50%",
      animation: `${waveAnimation} ${animationDuration}s ease-in-out ${
        -index * (parseFloat(animationDuration) / colors.length)
      }s infinite`,
    };
  };

  return (
    <Box {...styleContainer}>
      {colors.map((color, index) => (
        <Box key={"ball" + color} {...styleBall(color, index)} />
      ))}
    </Box>
  );
};

/**
 * Wave Spinner Block component
 *
 * A complete loading indicator that includes the spinner animation and loading text.
 * Styled for immediate visibility with proper z-index and positioning.
 *
 * @param props - WaveSpinnerBlock props
 */
export const WaveSpinnerBlock = ({
  title = "Processing...",
  spinnerParams,
  styleBlock,
  styleTitle,
}: WaveSpinnerBlockProps) => {
  const styleContainer: FlexProps = {
    w: "fit-content",
    h: "fit-content",
    flexDir: "column",
    gap: "24px",
    align: "center",
    justify: "center",
    position: "relative",
    zIndex: 10, // Ensures the spinner is visible above other content
    ...styleBlock,
  };

  const styleText: TextProps = {
    w: "100%",
    h: "100%",
    whiteSpace: "nowrap",
    color: "lightGray",
    textAlign: "center",
    ...h_700_16_100_32,
    ...styleTitle,
  };

  return (
    <Flex {...styleContainer}>
      <WaveSpinner {...spinnerParams} />
      <Text {...styleText}>{title}</Text>
    </Flex>
  );
};
